import moment from 'moment';
import { ShiftEvent, AggregatedEvent } from '../types/calendarTypes';
import { formatSkillCodes } from './skillUtils';

const getClaimedByUserNm = (events: ShiftEvent[]): string | undefined => {
    const claimedByNames = Array.from(
        new Set(events.map((event) => event.claimedByUserNm ?? event.claimedByName).filter(Boolean))
    );
    return claimedByNames.length > 0 ? claimedByNames.join(', ') : undefined;
};

/**
 * Aggregate shift events by date, shift type, status, time, team, and skillCd.
 * Shared between the manager calendar report and the gig-worker my-shifts view.
 */
export const aggregateEvents = (events: ShiftEvent[]): AggregatedEvent[] => {
    const grouped: Record<string, AggregatedEvent> = {};

    events.forEach(event => {
        const groupKey = [
            moment(event.date).format('YYYY-MM-DD'),
            event.shiftType,
            event.status,
            event.time,
            event.team ?? '',
            event.skillCd ?? '',
        ].join('-');

        if (grouped[groupKey]) {
            grouped[groupKey].resource.count++;
            grouped[groupKey].resource.events.push(event);
            grouped[groupKey].resource.claimedByUserNm = getClaimedByUserNm(grouped[groupKey].resource.events);
        } else {
            const startDate = moment(event.date).toDate();
            const endDate = moment(event.date).toDate();

            const titleParts = [event.time];
            if (event.shiftType) titleParts.push(event.shiftType);
            if (event.team) titleParts.push(`Team ${event.team}`);
            if (event.skillCd) titleParts.push(formatSkillCodes(event.skillCd));
            const title = titleParts.join(' - ');

            grouped[groupKey] = {
                id: groupKey,
                title,
                start: startDate,
                end: endDate,
                allDay: true,
                resource: {
                    shiftType: event.shiftType,
                    status: event.status,
                    time: event.time,
                    count: 1,
                    team: event.team,
                    skill: event.skill,
                    skillCd: event.skillCd,
                    skillDesc: event.skillDesc,
                    postedByUserId: event.postedByUserId,
                    postedByUserNm: event.postedByUserNm,
                    claimedByUserNm: getClaimedByUserNm([event]),
                    events: [event]
                }
            };
        }
    });

    return Object.values(grouped);
};

export const getStatusColor = (status: ShiftEvent['status']): string => {
    const colors: Record<ShiftEvent['status'], string> = {
        'APPROVED': '#4caf50',
        'POSTED': '#ffc107',
        'PENDING APPROVAL': '#ff9800',
        'REJECTED': '#f44336',
        'CANCELLED': '#9e9e9e'
    };
    return colors[status] || '#9e9e9e';
};

export const getAlertSeverity = (status: ShiftEvent['status']): 'success' | 'warning' | 'error' | 'info' => {
    const severityMap: Record<ShiftEvent['status'], 'success' | 'warning' | 'error' | 'info'> = {
        'APPROVED': 'success',
        'POSTED': 'info',
        'PENDING APPROVAL': 'warning',
        'REJECTED': 'error',
        'CANCELLED': 'info',
    };
    return severityMap[status] || 'info';
};

export const statusLegend: Array<{ status: ShiftEvent['status']; label: string }> = [
    { status: 'APPROVED', label: 'Approved' },
    { status: 'POSTED', label: 'Posted' },
    { status: 'PENDING APPROVAL', label: 'Pending Approval' },
    { status: 'REJECTED', label: 'Rejected' },
    { status: 'CANCELLED', label: 'Cancelled' },
];